<script setup lang="ts">
import { ref, computed, reactive, watch } from "vue";
import { breakpointsBootstrapV5, useBreakpoints, onClickOutside } from "@vueuse/core";
import { ButtonSeverity, ButtonSize, isNullish, TuButton, TuIconButton } from "@tinyurldev/tu-ui-vue";

import useNotify from "@Composables/useNotify.js";
import Capitalize from "@Resources/js/filters/Capitalize";
import useTranslation from "@Composables/useTranslation";
import { FetchOptions } from "@Resources/js/modules/teams/ViewModels/TeamUrlViewModel";

type Filter = {
  name: string;
  icon: string;
  action: NonNullable<FetchOptions["filter"]>;
  testId: string;
}

//
// Props
interface Props {
  filter?: FetchOptions["filter"];
  searchType?: FetchOptions["searchType"];
  search?: string;
}

const props = withDefaults(defineProps<Props>(), {
  filter: "available",
  searchType: "alias",
  search: "",
});

//
// Emits
interface Events {
  (e: "search", state: FetchOptions): void;
  (e: "input-error"): void;
  (e: "download"): void;
}

const emit = defineEmits<Events>();

//
// Setup
const { notifyError } = useNotify();
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Data
const isAliasOpen = ref(false);
const isFilterOpen = ref (false);
const dropdownFilterRef = ref<HTMLElement>();
const dropdownAliasRef = ref<HTMLElement>();

const form = reactive<Required<Pick<FetchOptions, "search">>>({
  search: props.search ?? "",
});

//
// Watchers
watch(() => props.search, (search) => {
  form.search = search ?? "";
});

//
// Computed
/**
 * We create the form as a computed property because the $t translation method does not seem to exist at
 * data() time
 */
const formParams = computed(() => {
  return {
    filters: [
      {
        name: __("urls.filter.available"),
        icon: "fa-globe",
        action: "available" as const,
        testId: "dashboard_sidebar_status_available_btn",
      },
      {
        name: __("urls.filter.archived"),
        icon: "fa-archive",
        action: "archived" as const,
        testId: "dashboard_sidebar_status_archive_btn",
      },
    ],
  };
});

/**
 * The currently chosen filter (uses props directly)
 */
const currentFilter = computed(() => formParams.value.filters.find((i) => i.action === props.filter));

onClickOutside(dropdownAliasRef, () => {
  if (isAliasOpen.value) {
    toggleAliasDropdown();
  }
});

onClickOutside(dropdownFilterRef, () => {
  if(isFilterOpen.value) {
    toggleFilterDropdown();
  }
});

//
// Methods
const clearValue = () => {
  if (form.search) {
    form.search = "";

    applyFilters();
  }
};

/**
 * Emits the control state, dropping a search term the server would reject so that changing a
 * control is never blocked by what happens to be typed in the search box.
 */
function applyFilters(overrides: Partial<Pick<FetchOptions, "filter" | "searchType">> = {}): void {
  const input = form.search.trim();

  emit("search", {
    filter: props.filter,
    searchType: props.searchType,
    search: input.length < 2 ? "" : input,
    ...overrides,
  });
}

function submitSearch(): void {
  if (form.search.trim().length === 1) {
    const error = "The search string must be at least 2 characters.";

    notifyError({ text: error });
    emit("input-error");
  }

  applyFilters();
};

function toggleAliasDropdown(isVisible?: boolean): void {
  if (isNullish(isVisible)) {
    isAliasOpen.value = !isAliasOpen.value;

    return;
  }

  isAliasOpen.value = isVisible;
}

function toggleFilterDropdown(isVisible?: boolean): void {
  if (isNullish(isVisible)) {
    isFilterOpen.value = !isFilterOpen.value;

    return;
  }

  isFilterOpen.value = isVisible;
}

const selectSearchType = (value: NonNullable<FetchOptions["searchType"]>) => {
  toggleAliasDropdown(false);
  applyFilters({ searchType: value });
};

const selectFilterState = (filter: Filter) => {
  toggleFilterDropdown(false);
  applyFilters({ filter: filter.action });
};
</script>

<template>
  <div
    class="v2-url-filter"
    :class="{ 'v2-url-filter--mobile': isMobileView }"
  >
    <!-- Search Type -->
    <div class="input-group input-group-sm">
      <div class="input-group-prepend">
        <button
          ref="dropdownAliasRef"
          data-test-id="dashboard_sidebar_search_by"
          class="_search_type btn btn-blue btn-sm v2-url-filter__dropdown-button"
          :class="`_current_search_type_${searchType}`"
          type="button"
          @click="() => toggleAliasDropdown()"
        >
          <span>
            {{ Capitalize(searchType ?? '') }}
            <i
              class="fa-solid"
              :class="[
                {
                  'fa-chevron-up': isAliasOpen,
                  'fa-chevron-down': !isAliasOpen
                }
              ]"
            />
          </span>
        </button>
        <div
          v-show="isAliasOpen"
          class="dropdown-menu show py-2 v2-url-filter__dropdown-menu"
        >
          <button
            v-for="filterType in ['alias', 'tag']"
            :key="filterType"
            :class="`_search_type_${filterType} v2-url-filter__dropdown-item dropdown-item py-0 px-3`"
            type="button"
            @click.prevent="
              // @ts-ignore we cannot fix types in templates in vue test utils v1
              selectSearchType(filterType)"
          >
            {{ Capitalize(filterType) }}
          </button>
        </div>
      </div>
      <div class="v2-url-filter__input-wrapper border-0 flex-center-center form-control h-100 position-relative">
        <i
          class="_url-filter-search-icon v2-url-filter__search-icon fa-solid fa-magnifying-glass"
          @click="submitSearch"
        />
        <input
          v-model="form.search"
          data-test-id="dashboard_sidebar_search_input"
          type="text"
          class="_url-filter-input v2-url-filter__input"
          aria-label="Text input with dropdown button"
          placeholder="Search"
          @keyup.enter="submitSearch"
        >
        <i
          v-if="form.search"
          class="_clear_search v2-url-filter__clear-icon fa-solid fa-close fa-lg"
          @click="clearValue"
        />
      </div>
    </div>

    <!-- Filter By -->
    <div class="dropdown">
      <TuButton
        ref="dropdownFilterRef"
        data-test-id="dashboard_sidebar_status"
        class="_status_filter v2-url-filter__dropdown-filter"
        :class="`_current_filter_${filter}`"
        label=""
        is-outlined
        :size="ButtonSize.LARGE"
        :severity="ButtonSeverity.WHITE"
        @click="() => toggleFilterDropdown()"
      >
        <div class="v2-url-filter__dropdown-content">
          <i
            v-if="currentFilter"
            class="_filter_by fa-solid"
            :class="currentFilter.icon"
          />
          <span
            v-if="currentFilter?.name && !isMobileView"
            class="v2-url-filter__dropdown-filter"
          >
            {{ Capitalize(currentFilter.name) }}
          </span>
          <i
            class="fa-solid"
            :class="[
              {
                'fa-chevron-up': isFilterOpen,
                'fa-chevron-down': !isFilterOpen
              }
            ]"
          />
        </div>
      </TuButton>
      <div
        class="_status_menu dropdown-menu dropdown-menu-right py-2 v2-url-filter__dropdown-menu"
        :class="{ show: isFilterOpen }"
      >
        <button
          v-for="filterOption in formParams.filters"
          :key="filterOption.action"
          :data-test-id="filterOption.testId"
          :class="`_status_${filterOption.action} dropdown-item py-0 px-3`"
          type="button"
          @click="selectFilterState(filterOption)"
        >
          <i
            class="fa-solid"
            :class="filterOption.icon"
          />
          <span class="ml-1">{{ Capitalize(filterOption.name) }}</span>
        </button>
      </div>
    </div>

    <!-- Download -->
    <TuIconButton
      v-tooltip="{ content: 'Download TinyURLs in bulk' }"
      class="_url-filter-download-button v2-url-filter__download-button"
      icon-class="fa-solid fa-cloud-arrow-down"
      is-outlined
      :size="ButtonSize.LARGE"
      :severity="ButtonSeverity.WHITE"
      @click="emit('download')"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/position.scss" as position;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.v2-url-filter{
  $self: &;

  display: flex;
  gap: spacing.$xxxs;
  align-items: stretch;
  padding: spacing.$xxxs spacing.$s;

  background-color: color.$accent-primary;

  &__dropdown-content {
    display: flex;
    gap: spacing.$xxxs;
    align-items: center;

    font-size: typography.$xs-font-size;
    line-height: typography.$xs-line-height;
    font-weight: typography.$font-weight-medium;
  }

  &__dropdown-menu {
    z-index: position.$higher-z-index;
  }

  &__dropdown-button {
    z-index: position.$lower-z-index;

    span {
      display: flex;
      align-items: center;
      gap: spacing.$xxxs;

      font-size: typography.$xs-font-size;
      line-height: typography.$xs-line-height;
      font-weight: typography.$font-weight-medium;
    }

    &:focus {
      z-index: position.$lower-z-index;
    }
  }

  &__input-wrapper {
    gap: spacing.$xxxs;
    padding: spacing.$xxxxs spacing.$xxxs;
  }

  &__input {
    width: 100%;
    border: none;

    font-size: typography.$xs-font-size;
    line-height: typography.$xs-line-height;
    font-weight: typography.$font-weight-medium;
  }

  &__search-icon,
  &__clear-icon {
    color: color.$text;
    font-size: typography.$base-font-size;

    cursor: pointer;
  }

  &--mobile {
    padding: spacing.$xxxs;

    #{ $self }__dropdown-content {
      font-size: typography.$base-font-size;
      line-height: typography.$base-line-height;
    }
  }
}
</style>
